import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Paper, Typography, Button, Container, Alert, Stack } from '@mui/material';
import {
  Lock as LockIcon,
  Home as HomeIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';

// Shared hooks
import { useAuth } from '../../hooks/auth/useAuth';

/**
 * Unauthorized Access Page
 *
 * Displays when a user tries to access a resource they don't have permission for.
 * Provides context about why access was denied and options to navigate elsewhere.
 */
const UnauthorizedPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, currentOrgName, getRoleNames, isAuthenticated } = useAuth();

  // Get error details from location state (set by ProtectedRoute)
  const reason = location.state?.reason || 'You do not have permission to access this resource.';
  const fromPath = location.state?.from?.pathname || '/';

  const userRoles = getRoleNames();

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleGoHome = () => {
    navigate('/dashboard');
  };

  const handleLogin = () => {
    navigate('/login');
  };

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 6,
            borderRadius: 3,
            maxWidth: 600,
            width: '100%',
          }}
        >
          {/* Lock Icon */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 3,
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: 'error.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
              }}
            >
              <LockIcon sx={{ fontSize: 40, color: 'error.contrastText' }} />
            </Box>
          </Box>

          {/* Title */}
          <Typography variant="h4" component="h1" gutterBottom>
            Access Denied
          </Typography>

          {/* Reason */}
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            {reason}
          </Typography>

          {/* User Info Alert (if authenticated) */}
          {isAuthenticated && user && (
            <Alert severity="info" sx={{ mb: 4, textAlign: 'left' }}>
              <Stack spacing={1}>
                <Typography variant="body2">
                  <strong>User:</strong> {user.username} ({user.first_name} {user.last_name})
                </Typography>
                {currentOrgName && (
                  <Typography variant="body2">
                    <strong>Organization:</strong> {currentOrgName}
                  </Typography>
                )}
                {userRoles.length > 0 && (
                  <Typography variant="body2">
                    <strong>Roles:</strong> {userRoles.join(', ')}
                  </Typography>
                )}
              </Stack>
            </Alert>
          )}

          {/* Actions */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="center"
            sx={{ mt: 4 }}
          >
            {isAuthenticated ? (
              <>
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={handleGoBack}
                  sx={{ minWidth: 120 }}
                >
                  Go Back
                </Button>
                <Button
                  variant="contained"
                  startIcon={<HomeIcon />}
                  onClick={handleGoHome}
                  sx={{ minWidth: 120 }}
                >
                  Go to Dashboard
                </Button>
              </>
            ) : (
              <Button variant="contained" onClick={handleLogin} sx={{ minWidth: 120 }}>
                Sign In
              </Button>
            )}
          </Stack>

          {/* Additional Info */}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 4, fontStyle: 'italic' }}>
            If you believe this is an error, please contact your system administrator.
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
};

export default UnauthorizedPage;
